import type { Ref } from 'react';
import { useI18n } from '../../i18n';
import { birthDateMin, todayLocal } from '../../utils/calendarDate';
import CountryPicker from './CountryPicker';
import Field from './Field';
import { isValidCountry } from '../../lib/countries';
import type { CreateAthleteRecordDto, Gender } from '../../types';

/** Form-state shape owned by the parent. All fields are kept as strings so
 *  `<input>`s stay controlled — the parent coerces to numeric/Gender at
 *  submit time. */
export interface AthleteFieldsForm {
  first_name: string;
  last_name: string;
  gender: string;
  birth_date: string;
  experience_bouts: string;
  current_weight_kg: string;
  country: string;
}

/** The blank form of a new athlete record (#664). The experience starts at 0
 *  and the country at LT, as the server defaults do. */
export const EMPTY_RECORD_FORM: AthleteFieldsForm = {
  first_name: '', last_name: '', gender: '', birth_date: '',
  experience_bouts: '0', current_weight_kg: '', country: 'LT',
};

/** The body of `createAthleteRecord()` from the form (#664). A create flow
 *  never builds this payload by hand. The gender narrowing is sound: the
 *  select emits '' | 'Male' | 'Female', and `required` (or the JS check of the
 *  caller) stops the ''. */
export function toCreateRecordDto(form: AthleteFieldsForm): CreateAthleteRecordDto {
  return {
    first_name: form.first_name,
    last_name: form.last_name,
    gender: form.gender as Gender,
    birth_date: form.birth_date,
    experience_bouts: parseInt(form.experience_bouts) || 0,
    current_weight_kg: parseFloat(form.current_weight_kg) || undefined,
    country: form.country,
  };
}

/** The limits of the number inputs mirror the rules of the server
 *  (`r.weight()`: above 0, at most 200; `r.experience()`: 0 to 1000). */
const MAX_WEIGHT_KG = 200;
/** Register Boxers (`BulkRegisterModal`) reads it too (#674). */
export const MAX_EXPERIENCE_BOUTS = 1000;

interface AthleteFieldsGroupProps {
  form: AthleteFieldsForm;
  /** Called with a patch function: `onChange(prev => ({ ...prev, field: val }))`.
   *  Parent can own a larger form (e.g. Admin.tsx user-form with email/phone)
   *  and merge the patch — the patch only references `AthleteFieldsForm`
   *  keys so the merge is safe. */
  onChange: (updater: (prev: AthleteFieldsForm) => AthleteFieldsForm) => void;
  /** The names, the gender and the birth date are required: the HTML `required`
   *  and a ` *` on the label (#664, bug 7). The create flows set it. The edit
   *  form leaves it off, and the server refuses an empty birth date. */
  required?: boolean;
  /** The focus target of the first field (the Register Boxers overlay). */
  firstFieldRef?: Ref<HTMLInputElement>;
  /** The test ids of the two name fields (the Add athlete record modal). */
  testIds?: { first?: string; last?: string };
  /** #674 — the lowest bout count that the user can enter. The edit form
   *  passes the stored count when the user cannot lower it (not an admin and
   *  not a team manager). The input gets this `min`. The server decides: it
   *  refuses a lower count with 403, so the `min` is a hint. The create forms
   *  leave it out: the `min` is then 0. */
  minExperience?: number;
}

export default function AthleteFieldsGroup({
  form, onChange, required, firstFieldRef, testIds, minExperience,
}: AthleteFieldsGroupProps) {
  const { t } = useI18n();
  const set = (field: keyof AthleteFieldsForm, value: string) =>
    onChange(f => ({ ...f, [field]: value }));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Field label={t.profile.firstName} required={required}>
          <input ref={firstFieldRef} className="input" value={form.first_name}
            data-testid={testIds?.first} onChange={e => set('first_name', e.target.value)} />
        </Field>
        <Field label={t.profile.lastName} required={required}>
          <input className="input" value={form.last_name}
            data-testid={testIds?.last} onChange={e => set('last_name', e.target.value)} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label={t.common.gender} required={required}>
          <select className="input" value={form.gender} data-testid="athlete-gender"
            onChange={e => set('gender', e.target.value)}>
            <option value="">—</option>
            <option value="Male">{t.common.male}</option>
            <option value="Female">{t.common.female}</option>
          </select>
        </Field>
        <Field label={t.profile.birthDate} required={required}>
          <input type="date" className="input" min={birthDateMin()} max={todayLocal()}
            value={form.birth_date} data-testid="athlete-birth-date" onChange={e => set('birth_date', e.target.value)} />
        </Field>
      </div>
      <div className="grid grid-cols-2 items-end gap-2">
        <Field label={t.profile.experienceBouts}>
          <input type="number" min={minExperience ?? 0} max={MAX_EXPERIENCE_BOUTS} className="input" value={form.experience_bouts} onChange={e => set('experience_bouts', e.target.value)} data-testid="athlete-experience-input" />
        </Field>
        <Field label={<>{t.common.weight} (kg)</>}>
          <input type="number" min="0.1" max={MAX_WEIGHT_KG} step="0.1" className="input" placeholder="71.5" value={form.current_weight_kg} onChange={e => set('current_weight_kg', e.target.value)} data-testid="athlete-weight" />
        </Field>
      </div>
      <Field label={t.country.label}>
        <CountryPicker
          value={isValidCountry(form.country) ? form.country : 'LT'}
          onChange={code => set('country', code)}
        />
      </Field>
    </div>
  );
}
